import { Component, OnInit, OnDestroy, Input, ViewChild, OnChanges, SimpleChanges, ElementRef, Renderer2, ComponentRef, ApplicationRef, HostListener } from '@angular/core';
import { UIRouter } from '@uirouter/angular';

import { GfTranslateService } from '../../../core/services/core.translate.service';
import { DynamicComponentLoaderService } from '../../../core/services/core.dynamic.component.loader.service';
import { StateHelperService } from '../../../core/services/core.ui.state.service';
import { MarkdownEditorService } from '../../../core/services/core.markdown-editor.service';
import { MACRO_REGISTRY, MacroDefinition, MacroContext } from '../services/wiki.macro.registry';

interface MacroInstance {
    token: string;
    placeholderId: string;
    definition: MacroDefinition;
    macroParam?: string;
}

@Component({
    selector: 'view-wiki',
    template: `<div class="wiki-content" #wikiContainer></div>`
})
export class WikiViewHtmlComponent implements OnInit, OnDestroy, OnChanges {
    @ViewChild('wikiContainer', { read: ElementRef, static: true }) wikiContainer!: ElementRef;

    @Input() wikiHtml: any;
    @Input() inputs: MacroContext = {};

    defaultContent: string = '';
    identifier: string = '';
    private renderedMacros: MacroInstance[] = [];
    private dynamicComponentRefs: ComponentRef<any>[] = [];

    constructor(
        private gfTranslateService: GfTranslateService,
        private dynamicComponentLoaderService: DynamicComponentLoaderService,
        private renderer: Renderer2,
        private appRef: ApplicationRef,
        private router: UIRouter,
        private stateHelperService: StateHelperService,
        private markdownEditorService: MarkdownEditorService
    ) {
        // TODO Gracefully handle when wikiHtml is actually empty
        this.defaultContent = '<p>' + this.gfTranslateService.translate('There is no content for this wiki page yet') + '</p>';
    }

    /**
     * Intercept clicks on <a> elements inside dynamically rendered wiki HTML.
     * Internal links are routed through UIRouter for SPA transitions instead
     * of triggering a full-page reload.
     */
    @HostListener('click', ['$event'])
    onContentClick(event: MouseEvent): void {
        if (event.button !== 0) return;

        const anchor = (event.target as HTMLElement).closest('a') as HTMLAnchorElement;
        if (!anchor) return;

        const href = anchor.getAttribute('href');
        if (!href || href.startsWith('mailto:') || href.startsWith('tel:')) return;

        // <base href="/"> resolves bare #anchor to the home page — intercept and scroll instead.
        if (href.startsWith('#')) {
            event.preventDefault();
            this.stateHelperService.applyHash(href);
            return;
        }

        // Modifier-key clicks (Ctrl/Cmd/Shift/Alt = new tab/window/save) — defer to the
        // browser's native behavior for non-fragment links.
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

        if (anchor.getAttribute('target')) return;

        try {
            const url = new URL(href, window.location.origin);
            if (url.origin !== window.location.origin) return;
            if (url.pathname.startsWith('/api/')) return;

            // Only intercept if UIRouter recognises this URL as a known state.
            // Otherwise let the browser handle it normally.
            const searchParams: { [key: string]: any } = {};
            url.searchParams.forEach((value, key) => { searchParams[key] = value; });
            const matched = this.router.urlService.match({ path: url.pathname, search: searchParams });
            if (matched && matched.rule && matched.rule['state']) {
                event.preventDefault();
                this.stateHelperService.navigateByUrl(href);
            }
        } catch (e) {
            // Invalid URL — let the browser handle it
        }
    }

    ngOnInit(): void {
        // Use a hex-based 6 digit random string
        const rando = Math.floor(Math.random() * 16777215).toString(16);
        this.identifier = 'wiki-view-content-' + rando;
        if (this.wikiHtml) {
            this.setWikiDisplay(this.wikiHtml);
        }
    }

    ngOnDestroy(): void {
        this.destroyDynamicComponents();
    }

    private destroyDynamicComponents(): void {
        this.dynamicComponentRefs.forEach((ref) => {
            try {
                this.appRef.detachView(ref.hostView);
                ref.destroy();
            } catch (e) {
                // Component may already be destroyed
            }
        });
        this.dynamicComponentRefs = [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        const htmlChanged = (changes.wikiHtml?.currentValue !== undefined);
        const inputsChanged = !!changes.inputs;

        if (htmlChanged || inputsChanged) {
            this.setWikiDisplay(this.wikiHtml);
        }
    }

    /**
     * Converts macro-rich wiki HTML into DOM markup and attaches the Angular components registered for
     * each recognised macro token.
     *
     * @param content The raw wiki HTML which may contain tokens (e.g. `%%PROJECT_TRACKERS%%`)
     * @example
     * // Input: wiki HTML string containing macros. Output: DOM updated via Renderer2, no return value.
     * this.setWikiDisplay('Welcome! %%PROJECT_ACTIVITY_CHART%%');
     */
    private setWikiDisplay(content: string): void {
        if (!this.identifier || !this.wikiContainer) return;

        // Clean up previously attached dynamic components before re-rendering
        this.destroyDynamicComponents();

        // Get the content or use default
        const wikiContent = content || this.defaultContent;

        // Replace macros with placeholder spans
        const context = this.inputs || {};
        const macroResult = this.replaceTokensWithPlaceholders(wikiContent, context);
        this.renderedMacros = macroResult.instances;

        // Wraps in a div container
        const wikiPage = `<div id="${this.identifier}">${macroResult.html}</div>`;

        // Renders the HTML
        this.renderer.setProperty(this.wikiContainer.nativeElement, 'innerHTML', wikiPage);

        // Syntax-highlight code blocks (with copy buttons), render Mermaid diagrams,
        // and render KaTeX math — mirrors what the ToastUI editor's own panes do.
        this.markdownEditorService.enhanceStaticHtml(this.wikiContainer.nativeElement);

        // Attaches dynamic components to the placeholder
        setTimeout(() => {
            this.renderedMacros.forEach(({ placeholderId, definition, macroParam }) => {
                const resolveContext = macroParam ? { ...context, macroParam } : context;
                const componentInputs = definition.resolveInputs ? definition.resolveInputs(resolveContext) : undefined;
                const ref = this.dynamicComponentLoaderService.attachDynamicComponent(placeholderId, definition.component, componentInputs);
                if (ref) {
                    this.dynamicComponentRefs.push(ref);
                }
            });
        }, 0);
    }

    private replaceTokensWithPlaceholders(content: string, context: MacroContext): { html: string; instances: MacroInstance[] } {
        let processedHtml = content;
        const instances: MacroInstance[] = [];

        Object.keys(MACRO_REGISTRY).forEach((token) => {
            const definition = MACRO_REGISTRY[token];

            if (definition.parameterized) {
                // Match parameterized macros: %%TOKEN(param)%%
                const regex = new RegExp(`%%${token}\\(([^)]+)\\)%%`, 'g');
                let match;
                let occurrenceIndex = 0;

                while ((match = regex.exec(processedHtml)) !== null) {
                    if (definition.shouldRender && !definition.shouldRender(context)) {
                        processedHtml = processedHtml.replace(match[0], '');
                        continue;
                    }

                    const macroParam = match[1].replace(/<[^>]*>/g, '');
                    const placeholderId = occurrenceIndex === 0
                        ? definition.placeholderId
                        : `${definition.placeholderId}-${occurrenceIndex}`;

                    processedHtml = processedHtml.replace(match[0], `<span id="${placeholderId}"></span>`);
                    instances.push({
                        token,
                        placeholderId,
                        definition,
                        macroParam
                    });
                    occurrenceIndex += 1;

                    // Reset regex since we modified the string
                    regex.lastIndex = 0;
                }
            } else {
                const marker = `%%${token}%%`;

                if (!processedHtml.includes(marker)) {
                    return;
                }

                if (definition.shouldRender && !definition.shouldRender(context)) {
                    processedHtml = processedHtml.split(marker).join('');
                    return;
                }

                let occurrenceIndex = 0;
                while (processedHtml.includes(marker)) {
                    const placeholderId = occurrenceIndex === 0
                        ? definition.placeholderId
                        : `${definition.placeholderId}-${occurrenceIndex}`;

                    processedHtml = processedHtml.replace(marker, `<span id="${placeholderId}"></span>`);
                    instances.push({ token, placeholderId, definition });
                    occurrenceIndex += 1;
                }
            }
        });

        processedHtml = processedHtml.replace(/%%[^%]+%%/g, '');

        return { html: processedHtml, instances };
    }
}
